// İSG uzmanı portalı — Firma yönetimi (api-contract §9.4, cross-tenant). Firma
// listesi (UTable sözleşmesi), oluştur (+opsiyonel ilk CorporateAdmin), düzenle;
// satır → firma detay (şube/saha hiyerarşisi §9.5/§9.6).

import {
  Alert,
  Button,
  Card,
  CardContent,
  type CompanyAdminDto,
  EmptyState,
  Pagination,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLink,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  adminApi,
  currentCulture,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { Building2, Plus, SearchX } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyFormModal } from '../../components/admin/CompanyFormModal';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { adminKeys } from '../../lib/adminKeys';
import { formatDate } from '../../lib/format';
import { useTableDensity } from '../../lib/uiPrefs';
import { useDebouncedValue } from '../../lib/useDebouncedValue';

const PAGE_SIZE = 20;

export function CompaniesPage() {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editCompany, setEditCompany] = useState<CompanyAdminDto | null>(null);

  function onSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  const params = {
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    sortBy: 'name',
    sortDir: 'asc' as const,
  };

  const query = useQuery({
    queryKey: adminKeys.companies(params),
    queryFn: () => adminApi.listCompanies(params),
    placeholderData: (prev) => prev,
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(debouncedSearch);
  function clearFilters() {
    setSearch('');
    setPage(1);
  }
  const culture = currentCulture();

  return (
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('admin.companies.new')}
        </Button>
      </ShellHeaderAction>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('admin.companies.count', { count: query.data.filteredTotal })
                : undefined
            }
            search={search}
            onSearchChange={onSearchChange}
            searchLabel={t('admin.common.search')}
            searchPlaceholder={t('admin.companies.searchPlaceholder')}
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
          />

          <Table caption={t('admin.companies.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.companies.col.name')}</TableHead>
                <TableHead>{t('admin.companies.col.legalName')}</TableHead>
                <TableHead>{t('admin.companies.col.taxNumber')}</TableHead>
                <TableHead>{t('admin.companies.col.createdAt')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('admin.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={5} />
              ) : query.isError ? (
                <TableMessageRow colSpan={5}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={5}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('admin.common.empty')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={Building2}
                      title={t('admin.common.noData')}
                      description={t('admin.common.noData.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('admin.companies.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((company) => (
                  <TableRow key={company.uuid}>
                    <TableCell>
                      {/* Birincil "aç" = ad hücresi (S33 Slice 4 QA fix, defekt #4) — eskiden
                          ayrı bir "Detay" düğmesiydi, hedefi gerçek bir rota. */}
                      <TableLink to={`/admin/companies/${company.uuid}`} className="block">
                        {company.name}
                      </TableLink>
                    </TableCell>
                    <TableCell className="text-muted">{company.legalName ?? '—'}</TableCell>
                    <TableCell className="text-muted">{company.taxNumber ?? '—'}</TableCell>
                    <TableCell className="text-muted">
                      {formatDate(company.createdAt, culture)}
                    </TableCell>
                    <TableCell className="w-12 px-2">
                      <RowActionsMenu
                        ariaLabel={t('admin.companies.action.openMenu', { name: company.name })}
                        items={[
                          {
                            key: 'edit',
                            label: t('admin.common.edit'),
                            onSelect: () => {
                              setMessage(null);
                              setEditCompany(company);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('admin.pagination.nav'),
                previous: t('admin.pagination.previous'),
                next: t('admin.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <CompanyFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => setMessage(t('admin.companies.created'))}
      />
      <CompanyFormModal
        open={editCompany !== null}
        onOpenChange={(open) => !open && setEditCompany(null)}
        company={editCompany}
        onSuccess={() => setMessage(t('admin.companies.updated'))}
      />
    </div>
  );
}
